import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, User, Headphones, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "splitara_chat_session";
const MODEL_KEY = "splitara_chat_model";

const MODELS = [
  { id: "gpt-5", label: "GPT-5", provider: "OpenAI" },
  { id: "claude-sonnet-4-5", label: "Claude Sonnet 4.5", provider: "Anthropic" },
  { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash", provider: "Google" },
];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [mode, setMode] = useState("ai");
  const [model, setModel] = useState(() => localStorage.getItem(MODEL_KEY) || "gpt-5");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [modelMenu, setModelMenu] = useState(false);
  const scrollRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Persist model choice
  useEffect(() => { localStorage.setItem(MODEL_KEY, model); }, [model]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Initialize session when widget opens for the first time
  useEffect(() => {
    if (!open) return;
    if (sessionId) return;

    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) {
      setSessionId(existing);
      loadMessages(existing);
    } else {
      createSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll while open for human replies / server-side updates
  useEffect(() => {
    if (!open || !sessionId) return;
    const tick = async () => {
      try {
        const lastAt = messages.length ? messages[messages.length - 1].created_at : "";
        const url = `${API}/chat/messages/${sessionId}${lastAt ? `?since=${encodeURIComponent(lastAt)}` : ""}`;
        const res = await fetch(url);
        if (!res.ok) return;
        const newer = await res.json();
        if (Array.isArray(newer) && newer.length) {
          setMessages((prev) => [...prev, ...newer.filter((n) => !prev.some((p) => p.id === n.id))]);
        }
      } catch (e) { /* noop */ }
    };
    pollTimerRef.current = setInterval(tick, 5000);
    return () => clearInterval(pollTimerRef.current);
  }, [open, sessionId, messages]);

  const createSession = async () => {
    try {
      const res = await fetch(`${API}/chat/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model }),
      });
      const data = await res.json();
      setSessionId(data.id);
      setMode(data.mode);
      localStorage.setItem(STORAGE_KEY, data.id);
      await loadMessages(data.id);
    } catch (e) { /* noop */ }
  };

  const loadMessages = async (sid) => {
    try {
      const res = await fetch(`${API}/chat/messages/${sid}`);
      const data = await res.json();
      if (Array.isArray(data)) setMessages(data);
    } catch (e) { /* noop */ }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setInput("");
    setSending(true);

    // Optimistic user message
    const optimistic = {
      id: `local-${Date.now()}`,
      session_id: sessionId,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);

    // Streaming assistant placeholder
    const streamId = `stream-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: streamId, session_id: sessionId, role: "assistant", content: "", created_at: new Date().toISOString(), _streaming: true },
    ]);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, text, model }),
      });
      if (!res.ok || !res.body) throw new Error("Bad response");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const evt = JSON.parse(jsonStr);
            if (evt.delta) {
              acc += evt.delta;
              setMessages((prev) => prev.map((m) => (m.id === streamId ? { ...m, content: acc } : m)));
            }
            if (evt.mode === "human") setMode("human");
            if (evt.done) { /* finalize */ }
          } catch (e) { /* noop */ }
        }
      }
      // Replace streaming placeholder with final via refetch (so id matches server)
      await loadMessages(sessionId);
    } catch (e) {
      setMessages((prev) => prev.map((m) => (m.id === streamId ? { ...m, content: "Sorry, something went wrong. Please try again." } : m)));
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startFresh = async () => {
    localStorage.removeItem(STORAGE_KEY);
    setSessionId(null);
    setMessages([]);
    setMode("ai");
    await createSession();
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat"
        className={`fixed bottom-20 right-6 z-[60] w-14 h-14 rounded-full grid place-items-center shadow-xl transition-all duration-300 ${
          open ? "bg-emerald-900 rotate-90" : "bg-emerald-800 hover:bg-emerald-700 hover:scale-105"
        }`}
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-300 border-2 border-emerald-50 animate-pulse" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-40 right-6 z-[60] w-[92vw] max-w-[400px] h-[70vh] max-h-[620px] rounded-3xl bg-white shadow-2xl border border-black/10 flex flex-col overflow-hidden fade-in-up">
          {/* Header */}
          <div className="relative bg-gradient-to-br from-emerald-800 to-emerald-950 p-5 pb-4 text-white">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 grid place-items-center">
                  {mode === "human" ? <Headphones className="w-5 h-5 text-amber-200" /> : <Sparkles className="w-5 h-5 text-amber-200" />}
                </div>
                <div>
                  <div className="font-display font-semibold text-lg leading-tight">
                    {mode === "human" ? "Splitara support" : "Splitara concierge"}
                  </div>
                  <div className="text-xs text-white/70 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                    {mode === "human" ? "A human is here" : <>AI {"\u2022"} usually replies in seconds</>}
                  </div>
                </div>
              </div>
            </div>

            {/* Model selector */}
            {mode === "ai" && (
              <div className="mt-3 relative">
                <button
                  onClick={() => setModelMenu((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 px-3 py-1.5 text-xs"
                >
                  <span className="opacity-80">Model:</span>
                  <span className="font-medium">{MODELS.find((m) => m.id === model)?.label}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${modelMenu ? "rotate-180" : ""}`} />
                </button>
                {modelMenu && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-black/10 overflow-hidden z-10">
                    {MODELS.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => { setModel(m.id); setModelMenu(false); }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 flex items-center justify-between ${model === m.id ? "bg-emerald-50" : ""}`}
                      >
                        <div>
                          <div className="text-emerald-950 font-medium">{m.label}</div>
                          <div className="text-emerald-950/50 text-[10px]">{m.provider}</div>
                        </div>
                        {model === m.id && <span className="text-emerald-700">{"\u2713"}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#faf7f0]">
            {messages.length === 0 && (
              <div className="text-center text-emerald-950/40 text-sm py-8">
                Starting your conversation{"\u2026"}
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}
          </div>

          {/* Input */}
          <div className="border-t border-black/5 bg-white p-3">
            <div className="flex items-end gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder={mode === "human" ? "Message the Splitara team\u2026" : "Ask me anything about Splitara\u2026"}
                disabled={sending || !sessionId}
                className="rounded-full h-11 border-black/10 focus-visible:ring-emerald-700"
              />
              <Button
                onClick={sendMessage}
                disabled={sending || !input.trim() || !sessionId}
                size="icon"
                className="rounded-full h-11 w-11 bg-emerald-900 hover:bg-emerald-800 shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-emerald-950/40 px-1">
              <span>Powered by AI {"\u2014"} may occasionally be wrong.</span>
              <button onClick={startFresh} className="hover:text-emerald-950/70 underline">New chat</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MessageBubble = ({ msg }) => {
  if (msg.role === "system") {
    return (
      <div className="flex justify-center">
        <div className="text-[11px] text-emerald-950/60 bg-emerald-50 rounded-full px-3 py-1 max-w-[85%] text-center">
          {msg.content}
        </div>
      </div>
    );
  }
  const isUser = msg.role === "user";
  const isHuman = msg.role === "human";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${isHuman ? "bg-amber-100" : "bg-emerald-100"}`}>
          {isHuman ? <Headphones className="w-3.5 h-3.5 text-amber-700" /> : <Sparkles className="w-3.5 h-3.5 text-emerald-800" />}
        </div>
      )}
      <div
        className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-emerald-900 text-white rounded-br-sm"
            : isHuman
            ? "bg-amber-50 text-emerald-950 border border-amber-100 rounded-bl-sm"
            : "bg-white text-emerald-950 border border-black/5 rounded-bl-sm"
        }`}
      >
        {msg.content || (msg._streaming ? <span className="inline-flex gap-1"><Dot /><Dot delay={150} /><Dot delay={300} /></span> : "")}
      </div>
      {isUser && (
        <div className="w-7 h-7 rounded-full bg-emerald-950 grid place-items-center shrink-0">
          <User className="w-3.5 h-3.5 text-white" />
        </div>
      )}
    </div>
  );
};

const Dot = ({ delay = 0 }) => (
  <span
    className="w-1.5 h-1.5 rounded-full bg-emerald-700 animate-bounce"
    style={{ animationDelay: `${delay}ms` }}
  />
);

export default ChatWidget;
